import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import YouTubeEmbed from './YouTubeEmbed';
import { FormattedContent } from '../utils/textUtils';
import { enhanceImageUrl } from '../utils/imageEnhancer';
import { handleLinkPreviewClick } from '../utils/readLaterUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostDetailModal = ({ post, categories, onClose, onPostModerated, onPostDeleted }) => {
  const { token, user, isAdminMode } = useAuth();
  const navigate = useNavigate();
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  const handleDeletePost = async (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!postToDelete) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/posts/${postToDelete.id}`, { headers });
      
      // Notify parent component about the deletion
      if (onPostDeleted) {
        onPostDeleted(postToDelete.id);
      } else {
        // Fallback: close modal and navigate
        onClose();
        navigate('/dashboard');
      }
      
      console.log('✅ Post deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting post:', error);
      console.log('❌ Failed to delete post. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };
  
  const [moderating, setModerating] = useState(false);
  
  // Hearts and Comments state
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Comment link preview state
  const [commentLinkPreview, setCommentLinkPreview] = useState(null);
  const [commentLinkTimeout, setCommentLinkTimeout] = useState(null);
  
  // Admin boost state
  const [showAdminBoost, setShowAdminBoost] = useState(false);
  const [boostSubmitting, setBoostSubmitting] = useState(false);
  
  // Check if current user can edit this post
  const canEdit = user && (user.username === post.user_id || isAdminMode);

  // Fetch like status and comments when modal opens
  useEffect(() => {
    if (post.id) {
      // Always fetch likes and comments data (they're now public endpoints)
      fetchLikeStatus();
      fetchComments();
    }
  }, [post.id]);

  const fetchLikeStatus = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // All posts in the main feed (including filler posts) use the posts endpoint
      const endpoint = `${API}/posts/${post.id}/likes`;
      
      const response = await axios.get(endpoint, { headers });
      setLiked(response.data.user_liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      console.error('Error fetching like status:', error);
      // Set defaults if fetch fails
      setLiked(false);
      setLikesCount(0);
    }
  };

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // All posts in the main feed (including filler posts) use the posts endpoint
      const endpoint = `${API}/posts/${post.id}/comments`;
      
      const response = await axios.get(endpoint, { headers });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLike = async () => {
    console.log('Heart clicked! Token:', !!token, 'Post ID:', post.id);
    
    if (!token) {
      alert('Please log in to like posts');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      // All posts in the main feed (including filler posts) use the posts endpoint
      const endpoint = `${API}/posts/${post.id}/like`;
      
      console.log('Making like request to:', endpoint);
      
      const response = await axios.post(endpoint, {}, { headers });
      console.log('Like response:', response.data);
      
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      console.error('Error toggling like:', error);
      alert('Failed to like post. Please try again.');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    console.log('Comment submit clicked! Token:', !!token, 'Comment:', newComment.trim());
    
    if (!newComment.trim() || submitting) {
      console.log('Comment submission blocked - empty comment or already submitting');
      return;
    }

    if (!token) {
      alert('Please log in to comment on posts');
      return;
    }

    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // All posts in the main feed (including filler posts) use the posts endpoint
      const endpoint = `${API}/posts/${post.id}/comments`;
      
      console.log('Making comment request to:', endpoint);
      
      const response = await axios.post(endpoint, {
        content: newComment.trim(),
        link_preview: commentLinkPreview && commentLinkPreview.editable ? {
          url: commentLinkPreview.url,
          title: commentLinkPreview.title,
          description: commentLinkPreview.description,
          image: commentLinkPreview.image,
          domain: commentLinkPreview.domain
        } : null
      }, { headers });

      console.log('Comment response:', response.data);

      if (!response.data.is_moderated) {
        setComments(prev => [...prev, response.data]);
        setNewComment('');
        setCommentLinkPreview(null); // Clear link preview
        console.log('Comment added successfully');
      } else {
        alert('Your comment has been flagged for review and will be visible once approved.');
        setNewComment('');
        setCommentLinkPreview(null); // Clear link preview
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Comment Link Preview Functions
  const detectURLsInComment = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex);
    return urls ? urls[0] : null; // Return first URL found
  };

  const generateCommentLinkPreview = async (url) => {
    try {
      setCommentLinkPreview({ url, loading: true, domain: new URL(url).hostname.replace('www.', '') });
      
      // Call backend to get rich preview data
      const response = await axios.post(`${API}/posts/link-preview`, { url });
      const previewData = response.data;
      
      if (previewData.error) {
        setCommentLinkPreview({
          url: url,
          domain: new URL(url).hostname.replace('www.', ''),
          title: previewData.headline || 'Link Preview',
          description: previewData.error,
          image: null,
          error: true
        });
      } else {
        setCommentLinkPreview({
          url: url,
          domain: previewData.site_name || new URL(url).hostname.replace('www.', ''),
          title: previewData.headline || 'Untitled',
          description: previewData.content || 'No description available',
          image: previewData.image,
          editable: true // Allow editing
        });
      }
      
      console.log('🔗 Comment link preview generated:', previewData.headline || 'No title');
    } catch (error) {
      console.log('Error generating comment link preview:', error);
      // Fallback to basic preview
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      
      setCommentLinkPreview({
        url: url,
        domain: domain,
        title: 'Link Preview',
        description: 'Click to edit title and description',
        editable: true
      });
    }
  };

  const handleCommentChange = (e) => {
    const content = e.target.value;
    setNewComment(content);
    
    // Clear previous timeout
    if (commentLinkTimeout) {
      clearTimeout(commentLinkTimeout);
    }
    
    // Detect URLs in comment
    const detectedURL = detectURLsInComment(content);
    
    if (detectedURL && detectedURL !== commentLinkPreview?.url) {
      // Set timeout for link detection (1.5 second debounce)
      const timeout = setTimeout(() => {
        generateCommentLinkPreview(detectedURL);
      }, 1500);
      setCommentLinkTimeout(timeout);
    } else if (!detectedURL) {
      // Clear link preview if no URL found
      setCommentLinkPreview(null);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const commentDate = new Date(dateString);
    const diff = now.getTime() - commentDate.getTime();
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const handleAdminBoost = async (boostLevel, adminBoostSize = null, reason = '') => {
    if (!isAdminMode) {
      alert('Admin privileges required');
      return;
    }

    setBoostSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/posts/${post.id}/admin-boost`, {
        boost_level: boostLevel,
        admin_boost_size: adminBoostSize,
        boost_reason: reason
      }, { headers });

      alert(`✅ Post boosted to ${boostLevel} level successfully!`);
      setShowAdminBoost(false);
      
      // Optionally refresh the post data or close modal
      // onClose();
      
    } catch (error) {
      console.error('Error boosting post:', error);
      alert('Failed to boost post. Please try again.');
    } finally {
      setBoostSubmitting(false);
    }
  };

  const handleEdit = () => {
    // Use React Router navigation instead of window.location.href
    navigate(`/create-post?edit=${post.id}`);
    onClose(); // Close the modal after navigating
  };

  const handleModerate = async (moderationStatus) => {
    if (!token) {
      alert('You must be logged in to moderate posts');
      return;
    }

    try {
      setModerating(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/posts/${post.id}/moderate`, {
        moderation_status: moderationStatus
      }, { headers });

      // Call callback to update the main feed
      onPostModerated(post.id);
      
      // Show success message
      const categoryName = moderationStatus.replace('_', ' ').replace('-', ' ');
      alert(`Post moved to ${categoryName} successfully`);
      
      onClose();
    } catch (error) {
      console.error('Error moderating post:', error);
      alert('Failed to moderate post. Please try again.');
    } finally {
      setModerating(false);
    }
  };

  const getCategoryLabels = (categoryKeys) => {
    if (!categoryKeys || !Array.isArray(categoryKeys)) return [];
    if (!categories || !Array.isArray(categories)) return categoryKeys;
    
    return categoryKeys.map(key => 
      categories.find(c => c.key === key)?.label || key
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        
        {/* Header - Mobile Optimized */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Post Details</h2>
          <div className="flex items-center space-x-2">
            {canEdit && (
              <button
                onClick={handleEdit}
                className="flex items-center space-x-1 px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs sm:text-sm font-medium min-h-[44px]"
              >
                <span>✏️</span>
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}
            
            {/* Save to Read Later Button */}
            {(post.link_preview || post.link_url) && token && (
              <button
                onClick={() => {
                  const linkData = post.link_preview || {
                    url: post.link_url,
                    title: post.title || 'Link',
                    description: null,
                    image: null,
                    domain: post.link_url ? new URL(post.link_url).hostname : null
                  };
                  handleLinkPreviewClick(linkData, post.id, 'post', token, user);
                }}
                className="flex items-center space-x-1 px-2 sm:px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-xs sm:text-sm font-medium min-h-[44px]"
                title="Save to Read Later File"
              >
                <span>📚</span>
                <span className="hidden lg:inline">Save to Read Later File</span>
                <span className="hidden sm:inline lg:hidden">Save to File</span>
              </button>
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl font-bold min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Post Author Info - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-4 sm:mb-6">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                post.is_official 
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600' 
                  : 'bg-gradient-to-br from-blue-400 to-cyan-500'
              }`}>
                <span className="text-white font-bold text-base sm:text-lg">
                  {post.is_official ? '🏢' : ((post.username || post.user_id)?.[0]?.toUpperCase() || 'U')}
                </span>
              </div>
              <div>
                <button
                  onClick={() => navigate(`/my-life?user=${post.username || post.user_id}`)}
                  className="font-semibold text-blue-600 hover:text-blue-800 text-base sm:text-lg underline transition-colors break-all"
                >
                  @{post.username || post.user_id}
                </button>
                <div className="text-xs sm:text-sm text-gray-500">
                  {new Date(post.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            {/* Edit/Delete Buttons for Own Posts */}
            {!post.isFiller && (post.username === user?.username || post.user_id === user?.id || isAdminMode) && (
              <div className="flex items-center space-x-2 ml-auto">
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/create-post?edit=${post.id}`);
                  }}
                  className="flex items-center space-x-1 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm font-medium">Edit</span>
                </button>
                <button
                  onClick={() => handleDeletePost(post)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-sm font-medium">Delete</span>
                </button>
              </div>
            )}
            
            {/* Badges - Mobile Responsive */}
            <div className="flex flex-wrap gap-2">
              {post.is_official && (
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  Official
                </span>
              )}
              {post.official_post_type && (
                <span className="bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  {post.official_post_type === 'announcement' ? '📢 Announcement' : 
                   post.official_post_type === 'article' ? '📖 Article' : 
                   post.official_post_type === 'guide' ? '📋 Guide' : 
                   post.official_post_type === 'update' ? '🔄 Update' : '📝 Post'}
                </span>
              )}
            </div>
          </div>

          {/* Categories */}
          {post.categories && post.categories.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {getCategoryLabels(post.categories).map((label, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Post Title */}
          <h1 className="text-3xl font-bold text-gray-800 mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Post Image */}
          {post.image && (
            <div className="mb-6">
              <img 
                src={enhanceImageUrl(post.image)} 
                alt={post.title}
                className="w-full h-auto object-contain rounded-xl shadow-lg max-h-screen"
              />
            </div>
          )}

          {/* YouTube Video */}
          {post.video_url && (
            <div className="mb-6">
              <YouTubeEmbed 
                videoUrl={post.video_url} 
                title={post.title}
                className="rounded-xl overflow-hidden shadow-lg"
              />
            </div>
          )}

          {/* Post Content */}
          <div className="mb-6">
            <FormattedContent 
              content={post.content}
              className="text-gray-700 text-lg leading-relaxed"
            />
          </div>

          {/* News Article Link */}
          {post.content_type === 'news' && post.link_url && (
            <div className="mb-6">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Always use window.open to preserve the platform feed
                  const newWindow = window.open(post.link_url, '_blank', 'noopener,noreferrer');
                  
                  if (!newWindow) {
                    // If popup is blocked, show user a message instead of navigating away
                    alert('Please allow popups for this site or copy this link: ' + post.link_url);
                  }
                }}
                className="w-full p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors border-2 border-emerald-200 flex items-center justify-center space-x-3"
              >
                <span className="text-emerald-600 text-xl">📰</span>
                <span className="text-emerald-700 font-medium">Open Original Article</span>
                <span className="text-emerald-500">↗</span>
              </button>
            </div>
          )}

          {/* Debug for news posts */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-2 p-2 bg-yellow-100 rounded text-xs">
              content_type: {post.content_type} | link_url: {post.link_url ? 'yes' : 'no'}
            </div>
          )}

          {/* Facebook-style Link Preview (for non-news posts) */}
          {((post.link_preview && post.content_type !== 'news') || (post.link_url && post.content_type !== 'news' && !post.link_preview)) && (
            <div className="mb-6">
              {post.link_preview ? (
                // Rich preview with image, title, description
                <a 
                  href={post.link_preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow bg-white"
                >
                  {/* Preview Image */}
                  {post.link_preview.image && (
                    <div className="w-full h-64 overflow-hidden">
                      <img 
                        src={post.link_preview.image} 
                        alt={post.link_preview.title || 'Link preview'} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  
                  {/* Preview Content */}
                  <div className="p-5">
                    <p className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-medium">
                      {post.link_preview.domain || 'External Link'}
                    </p>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight">
                      {post.link_preview.title || 'Untitled'}
                    </h3>
                    {post.link_preview.description && (
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {post.link_preview.description}
                      </p>
                    )}
                    <div className="flex items-center text-blue-600 font-medium">
                      <span>Visit Link</span>
                      <span className="ml-2 text-lg">↗</span>
                    </div>
                  </div>
                </a>
              ) : (
                // Fallback for basic link
                <a 
                  href={post.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-blue-500 text-xl">🔗</span>
                    <span className="text-blue-600 font-medium truncate">{post.link_url}</span>
                    <span className="text-blue-500 ml-auto">↗</span>
                  </div>
                </a>
              )}
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Interactive Hearts and Comments Section */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            {!token && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-blue-700 font-medium">Please log in to like posts and leave comments</p>
              </div>
            )}
            {/* Like Button */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={handleLike}
                className="flex items-center space-x-3 group cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                disabled={!token}
                style={{ cursor: token ? 'pointer' : 'not-allowed' }}
              >
                <span className={`text-3xl transition-all duration-200 group-hover:scale-110 ${
                  liked ? 'text-red-500' : 'text-blue-500'
                } ${!token ? 'opacity-50' : 'opacity-100'}`}>
                  {liked ? '❤️' : '💙'}
                </span>
                <span className={`text-lg font-medium ${!token ? 'text-gray-400' : 'text-gray-700'}`}>
                  {likesCount > 0 ? `${likesCount} ${likesCount === 1 ? 'like' : 'likes'}` : 'Be the first to like this'}
                </span>
              </button>
              
              <div className="text-sm text-gray-500">
                Posted {formatTimeAgo(post.created_at)}
              </div>
            </div>

            {/* Comments Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                💬 Comments ({comments.length})
              </h3>

              {/* Comment Form */}
              {token ? (
                <form onSubmit={handleCommentSubmit} className="mb-6">
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {user?.username?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={handleCommentChange}
                        placeholder="Share your thoughts... (URLs will be detected automatically)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows="3"
                        disabled={submitting}
                      />
                      
                      {/* Comment Link Preview */}
                      {commentLinkPreview && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start space-x-3">
                            <span className="text-blue-600 text-sm flex-shrink-0">🔗</span>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-800 text-sm">Link Preview</h4>
                                <button
                                  type="button"
                                  onClick={() => setCommentLinkPreview(null)}
                                  className="text-xs text-gray-500 hover:text-red-600"
                                >
                                  Remove
                                </button>
                              </div>
                              
                              {/* Preview Card */}
                              <div className="border rounded-lg overflow-hidden bg-white">
                                {commentLinkPreview.image && (
                                  <img 
                                    src={commentLinkPreview.image} 
                                    alt="Link preview" 
                                    className="w-full h-20 object-cover"
                                  />
                                )}
                                <div className="p-2">
                                  <p className="text-xs text-gray-500 mb-1">{commentLinkPreview.domain}</p>
                                  
                                  {/* Editable Title & Description */}
                                  {commentLinkPreview.editable ? (
                                    <div className="space-y-1">
                                      <input
                                        type="text"
                                        value={commentLinkPreview.title || ''}
                                        onChange={(e) => setCommentLinkPreview(prev => ({...prev, title: e.target.value}))}
                                        className="w-full text-xs font-medium text-gray-900 border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Edit title..."
                                      />
                                      <textarea
                                        value={commentLinkPreview.description || ''}
                                        onChange={(e) => setCommentLinkPreview(prev => ({...prev, description: e.target.value}))}
                                        rows="1"
                                        className="w-full text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 resize-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Edit description..."
                                      />
                                    </div>
                                  ) : (
                                    <div>
                                      <h5 className="text-xs font-medium text-gray-900 mb-1">
                                        {commentLinkPreview.title || 'Untitled'}
                                      </h5>
                                      <p className="text-xs text-gray-600 line-clamp-1">
                                        {commentLinkPreview.description || 'No description available'}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {commentLinkPreview.error && (
                                    <div className="mt-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                      {commentLinkPreview.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end mt-2">
                        <button
                          type="submit"
                          disabled={!newComment.trim() || submitting || !token}
                          className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 cursor-pointer ${
                            newComment.trim() && !submitting && token
                              ? 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          style={{ cursor: (newComment.trim() && !submitting && token) ? 'pointer' : 'not-allowed' }}
                        >
                          {submitting ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Posting...</span>
                            </div>
                          ) : (
                            'Post Comment'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-gray-600">Please log in to comment on this post</p>
                </div>
              )}

              {/* Comments List */}
              {commentsLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading comments...</p>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl mb-2 block">💬</span>
                  <p className="text-gray-500">No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment, index) => (
                    <div 
                      key={comment.id || index} 
                      className="flex space-x-3 p-4 bg-gray-50 rounded-xl"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-emerald-200 rounded-full flex items-center justify-center">
                          <span className="text-emerald-600 text-sm font-medium">
                            {(comment.username || comment.user_id || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-gray-800">
                            {comment.username || 'Anonymous'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatTimeAgo(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          {comment.content}
                        </p>
                        
                        {/* Display comment link preview if it exists */}
                        {comment.link_preview && (
                          <div className="mt-2 border rounded-lg overflow-hidden bg-white max-w-md">
                            {comment.link_preview.image && (
                              <img 
                                src={comment.link_preview.image} 
                                alt="Link preview" 
                                className="w-full h-16 object-cover"
                              />
                            )}
                            <div className="p-2">
                              <p className="text-xs text-gray-500 mb-1">{comment.link_preview.domain}</p>
                              <h5 className="text-xs font-medium text-gray-900 mb-1">
                                {comment.link_preview.title || 'Untitled'}
                              </h5>
                              <p className="text-xs text-gray-600 line-clamp-1">
                                {comment.link_preview.description || 'No description available'}
                              </p>
                              <a 
                                href={comment.link_preview.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                              >
                                Visit Link ↗
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Admin Boost Section (Admin Only) */}
          {isAdminMode && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-purple-800">👑 Admin Content Boost</h3>
                <button
                  onClick={() => setShowAdminBoost(!showAdminBoost)}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  <span>🚀</span>
                  <span className="font-medium">Boost This Post</span>
                  <svg className={`w-4 h-4 transition-transform ${showAdminBoost ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {showAdminBoost && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <p className="text-purple-700 mb-4 text-sm">
                    ✨ <strong>Admin Privilege:</strong> Boost posts with merit at no cost to promote quality content
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Standard Boost */}
                    <button
                      onClick={() => handleAdminBoost('standard', 'large', 'Quality content worthy of promotion')}
                      disabled={boostSubmitting}
                      className="p-4 bg-white rounded-lg border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-200 hover:shadow-lg disabled:opacity-50"
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">🌱</div>
                        <div className="font-semibold text-emerald-800">Standard Boost</div>
                        <div className="text-sm text-emerald-600 mt-1">24 hours • Larger size</div>
                        <div className="text-xs text-gray-500 mt-2">Good content</div>
                      </div>
                    </button>

                    {/* Premium Boost */}
                    <button
                      onClick={() => handleAdminBoost('premium', 'xl', 'Excellent content deserving featured placement')}
                      disabled={boostSubmitting}
                      className="p-4 bg-white rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-all duration-200 hover:shadow-lg disabled:opacity-50"
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">⭐</div>
                        <div className="font-semibold text-blue-800">Premium Boost</div>
                        <div className="text-sm text-blue-600 mt-1">48 hours • XL size</div>
                        <div className="text-xs text-gray-500 mt-2">Great content</div>
                      </div>
                    </button>

                    {/* VIP Boost */}
                    <button
                      onClick={() => handleAdminBoost('vip', 'featured', 'Outstanding content worthy of maximum visibility')}
                      disabled={boostSubmitting}
                      className="p-4 bg-white rounded-lg border-2 border-yellow-200 hover:border-yellow-400 transition-all duration-200 hover:shadow-lg disabled:opacity-50"
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">👑</div>
                        <div className="font-semibold text-yellow-800">VIP Boost</div>
                        <div className="text-sm text-yellow-600 mt-1">72 hours • Featured</div>
                        <div className="text-xs text-gray-500 mt-2">Exceptional content</div>
                      </div>
                    </button>
                  </div>

                  {boostSubmitting && (
                    <div className="mt-4 text-center">
                      <div className="inline-flex items-center space-x-2 text-purple-600">
                        <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Boosting post...</span>
                      </div>
                    </div>
                  )}

                  {/* Current Boost Status */}
                  {(post.admin_boosted || post.boost_level !== 'standard') && (
                    <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                      <div className="text-sm text-purple-800">
                        📊 <strong>Current Status:</strong> {post.boost_level?.toUpperCase() || 'STANDARD'} boost
                        {post.admin_boosted && (
                          <span className="ml-2 text-xs bg-purple-200 px-2 py-1 rounded-full">Admin Boosted</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI and Community Moderation Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🤖 AI & Community Moderation</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Help keep our community positive by moving negative content to the appropriate circles of Interhell. 
              These actions also serve as negative prompts for AI systems.
            </p>
            
            <h4 className="text-lg font-bold text-gray-800 mb-4">Four Horsemen of the Crapocalypse</h4>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-gray-700">
              <p className="mb-2">
                <strong>⚠️ Moderation Warning:</strong> By clicking one of these, this post will go into LIMBO for review. 
                If the comment passes review and returns to the platform, it is a demerit against you.
              </p>
              <p className="mb-2">
                If too many demerits pile up, your moderation ability will be given a time-out and if you accumulate too many time-outs, 
                your moderation ability may be revoked. You are too judgey and out of control, take a breath.
              </p>
              <p className="text-blue-600">
                <strong>BUT,</strong> if your Moderation ability is gone but you still want to as a consolation prize 
                we will give you a 50% discount on a set of <strong>Clutchin' Pearls ™</strong>, 
                the perfect prop for performative shock!
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Political Button */}
              <button
                onClick={() => handleModerate('political')}
                disabled={moderating}
                className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-100 to-red-200 hover:from-red-200 hover:to-red-300 rounded-lg border border-red-300 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              >
                <span className="text-2xl mb-2">🏛️</span>
                <span className="font-semibold text-red-800 text-sm mb-1">Political</span>
                <span className="text-red-600 text-xs text-center">
                  Too upsetting, it has a place, like EVERYWHERE it seems, just not here. With the exception of embedded podcasts and videos, they go there, but still best practices should keep you away from such content for at least the first hour of your day.
                </span>
              </button>

              {/* Spam Button */}
              <button
                onClick={() => handleModerate('spam')}
                disabled={moderating || (post.isFiller && post.content_type === 'ad')}
                className={`flex flex-col items-center justify-center p-4 rounded-lg border transition-all duration-300 hover:shadow-lg ${
                  (post.isFiller && post.content_type === 'ad') 
                    ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed' 
                    : 'bg-gradient-to-br from-orange-100 to-orange-200 hover:from-orange-200 hover:to-orange-300 border-orange-300 disabled:opacity-50'
                }`}
              >
                <span className="text-2xl mb-2">🔥</span>
                <span className={`font-semibold text-sm mb-1 ${
                  (post.isFiller && post.content_type === 'ad') ? 'text-gray-600' : 'text-orange-800'
                }`}>Spam</span>
                <span className={`text-xs text-center ${
                  (post.isFiller && post.content_type === 'ad') ? 'text-gray-500' : 'text-orange-600'
                }`}>
                  {(post.isFiller && post.content_type === 'ad') 
                    ? 'Advertisers are immune to this judgement, we gotta let them do their thing so we can keep giving you this for free.' 
                    : 'Toss that into its own private hell and let it sizzle. Advertisers are immune to this judgement, we gotta let them do their thing so we can keep giving you this for free.'
                  }
                </span>
              </button>

              {/* Conspiratorial Button */}
              <button
                onClick={() => handleModerate('conspiratorial')}
                disabled={moderating}
                className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-purple-100 to-purple-200 hover:from-purple-200 hover:to-purple-300 rounded-lg border border-purple-300 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              >
                <span className="text-2xl mb-2">🔍</span>
                <span className="font-semibold text-purple-800 text-sm mb-1">Conspiratorial</span>
                <span className="text-purple-600 text-xs text-center">
                  Almost everything is a conspiracy it seems, this is for those that could negatively affect our communities' health or well being.
                </span>
              </button>

              {/* Troll Hole Button */}
              <button
                onClick={() => handleModerate('troll_hole')}
                disabled={moderating}
                className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-lg border border-gray-300 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              >
                <span className="text-2xl mb-2">🕳️</span>
                <span className="font-semibold text-gray-800 text-sm mb-1">Troll Hole</span>
                <span className="text-gray-600 text-xs text-center">
                  Into the hole problem monster!
                </span>
              </button>
            </div>

            {moderating && (
              <div className="mt-4 text-center">
                <div className="inline-flex items-center space-x-2 text-blue-600">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Moving post...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Post
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this post? This action cannot be undone.
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostDetailModal;